import { AlertCircle, Lightbulb, RefreshCw } from "lucide-react";

import { Button } from "@/app/components/ui/button";
import RoleExplainer from "@/app/Views/StaticPages/HomePage/components/roleExplainer/RoleExplainer";

import { RoleExplanations } from "../utils/helpers";

interface RoleExplainerSectionProps {
	roleExplanations: RoleExplanations | null;
	isLoading: boolean;
	hasError?: boolean;
	onExplain: () => void;
	onFitCheckClick?: () => void;
	lang?: "en" | "sw";
	onLangChange?: (lang: "en" | "sw") => void;
}

export function RoleExplainerSection({
	roleExplanations,
	isLoading,
	hasError = false,
	onExplain,
	onFitCheckClick,
	lang,
	onLangChange,
}: RoleExplainerSectionProps) {
	// Loading State
	if (isLoading) {
		return (
			<div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl p-6 border border-primary-100 mb-6">
				<div className="flex items-start gap-4">
					<div className="p-2 bg-primary-100 rounded-lg">
						<Lightbulb className="h-5 w-5 text-primary-700 animate-pulse" />
					</div>
					<div className="flex-1">
						<h3 className="text-lg font-semibold text-gray-900 mb-2">Explaining the role...</h3>
						<p className="text-sm text-gray-600 mb-3">Breaking down responsibilities, expectations, and growth path.</p>
						<p className="text-xs text-gray-500">This won&apos;t submit an application.</p>

						{/* Loading animation */}
						<div className="mt-4 space-y-2">
							<div className="h-3 bg-primary-100 rounded animate-pulse w-3/4" />
							<div className="h-3 bg-primary-100 rounded animate-pulse w-1/2" />
							<div className="h-3 bg-primary-100 rounded animate-pulse w-2/3" />
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Error State
	if (hasError) {
		return (
			<div className="bg-red-50 rounded-xl p-6 border border-red-100 mb-6">
				<div className="flex items-start gap-4">
					<div className="p-2 bg-red-100 rounded-lg">
						<AlertCircle className="h-5 w-5 text-red-600" />
					</div>
					<div className="flex-1">
						<h3 className="text-lg font-semibold text-gray-900 mb-2">We couldn&apos;t explain the role right now</h3>
						<p className="text-sm text-gray-600 mb-4">
							Something didn&apos;t load properly. The official job description is still available below.
						</p>
						<Button onClick={onExplain} variant="outline" size="sm">
							<RefreshCw className="h-4 w-4 mr-2" />
							Try again
						</Button>
					</div>
				</div>
			</div>
		);
	}

	// Success State - Explanation displayed
	if (roleExplanations) {
		return (
			<div className="mb-6">
				{/* Success header */}
				<div className="mb-4">
					<h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
						<Lightbulb className="h-5 w-5 text-primary-600" />
						Here&apos;s a clearer explanation of the role
					</h3>
					<p className="text-sm text-gray-500 mt-1">
						Written to help you decide if this role fits you — not to sell it.
					</p>
				</div>

				{/* Role Explainer Content */}
				<RoleExplainer roleExplanations={roleExplanations} lang={lang} onLangChange={onLangChange} />

				{/* FitCheck CTA after explanation */}
				{onFitCheckClick && (
					<div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
						<p className="text-sm text-gray-700 mb-3">Now that you understand the role, see how well you match.</p>
						<Button onClick={onFitCheckClick} variant="outlineSecondary" size="sm">
							Check my fit for this role
						</Button>
					</div>
				)}
			</div>
		);
	}

	// Empty State - No explanation yet (shouldn't normally show since we moved CTA to action buttons)
	return null;
}
